import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Portal,
} from '@mui/material'
import { useContext, useState } from 'react'
import { ApiContext } from '../../contexts/ApiContext'
import { isValidGuid } from '../../utils/guid'
import { useAuth } from 'react-oidc-context'
import { SelectElement, TextFieldElement } from 'react-hook-form-mui'
import { KistillFormContainer } from '../Form/KistillFormContainer'
import { SelectItem, useStickerTypesQuery } from '@kistill/client/data-access'
import { useSnackbar } from 'notistack'
import Draggable from 'react-draggable'
import { useSortedItemsGuidsLazyQuery } from '@kistill/client/data-access'

interface StickersPopupProps {
  setOpen: (value: boolean) => void
  open: boolean
  handleClose: () => void
  itemGuids: readonly string[]
  sortConfig: {
    key: string
    direction: string
  }
}

export function StickersPopup({
  setOpen,
  open,
  handleClose,
  itemGuids,
  sortConfig,
}: StickersPopupProps) {
  const [stickerInfoGuid, setStickerInfoGuid] = useState<string | undefined>()
  const [startIndex, setStartIndex] = useState<number | undefined>(undefined)
  const [startLine, setStartLine] = useState<number | undefined>(undefined)
  const [copies, setCopies] = useState<number>(1)

  const [getSortedItems] = useSortedItemsGuidsLazyQuery()

  const { enqueueSnackbar } = useSnackbar()

  const { user } = useAuth()
  const apiPath = useContext(ApiContext)

  const { data } = useStickerTypesQuery()

  const stickerOptions: SelectItem[] = (data?.stickerTypes ?? []).map((x) => {
    return { id: x.id, title: x.title }
  })

  function setToFalse() {
    if (setOpen) {
      setOpen(false)
    }
  }

  const handleStickerDocument = async () => {
    const { data: sortedItemGuids } = await getSortedItems({
      variables: {
        selectedGuids: [...itemGuids],
        sort: sortConfig.direction,
        sortBy: sortConfig.key,
      },
    })

    if (data) {
      const selectedStickerTitle =
        stickerOptions.find((option) => option.id === stickerInfoGuid)?.title ||
        ''
      const line = startLine !== undefined ? startLine : undefined
      const index =
        startLine !== undefined
          ? undefined
          : startIndex !== undefined
            ? startIndex
            : 1
      const copiesCount = copies === undefined ? 1 : copies

      const sortedItems = sortedItemGuids?.sortedItemsGuids

      // Add index or line to the URL
      const startIndexUrl = index ? `&startIndex=${index}` : ''
      const startLineUrl = line ? `&startLine=${line}` : ''

      if (!stickerInfoGuid || !isValidGuid(stickerInfoGuid)) {
        enqueueSnackbar('Ógild tegund límmiða.', { variant: 'error' })
        return
      }

      const url = `${apiPath}/api/Items/Stickers?stickerInfoGuid=${encodeURIComponent(stickerInfoGuid)}${startIndexUrl}${startLineUrl}&copies=${copiesCount}`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.access_token}`,
        },
        body: JSON.stringify(sortedItems),
      })

      if (response.ok) {
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', `${selectedStickerTitle}.docx`)

        // Append to HTML link element page
        document.body.appendChild(link)

        // Start download
        link.click()

        // Clean up and remove the link
        document.body.removeChild(link)
        link.remove()

        // Success Snackbar
        enqueueSnackbar(
          `Skjal "${selectedStickerTitle}" sótt með ${itemGuids.length} stökum${
            copies !== 1 ? ` og ${copies} afritum.` : '.'
          }`,
          { variant: 'success' },
        )

        handleClose()
      } else {
        const responseBodyText = await response.text()
        enqueueSnackbar(
          `Villa kom upp við að sækja gögn. ${responseBodyText}`,
          { variant: 'error' },
        )
      }
    }
  }

  const handleIndexChange = (value: string) => {
    const numValue = Number(value)
    setStartIndex(numValue > 0 ? numValue : undefined)
    if (numValue > 0) {
      setStartLine(undefined)
    }
  }

  const handleLineChange = (value: string) => {
    const numValue = Number(value)
    setStartLine(numValue > 0 ? numValue : undefined)
    if (numValue > 0) {
      setStartIndex(undefined)
    }
  }

  const handleCopiesChange = (value: string) => {
    // Function to handle changes in the form rows
    setCopies(Number(value))
  }
  return (
    <Box style={{ position: 'relative' }}>
      <Portal>
        <Backdrop open={open} style={{ zIndex: 1300, position: 'fixed' }} />
      </Portal>
      <Draggable>
        <Dialog
          fullWidth
          open={open}
          onClose={() => setToFalse()}
          hideBackdrop={true}
          PaperProps={{
            sx: { maxWidth: 'none', width: '70%' },
          }}
        >
          <DialogTitle sx={{ pt: 5 }}>Sækja límmiða</DialogTitle>
          <DialogContent dividers>
            <KistillFormContainer onSuccess={handleStickerDocument}>
              <Grid container>
                <Grid item container spacing={2}>
                  <Grid item xs={12}>
                    <SelectElement
                      label='Tegund límmiða'
                      name='stickerType'
                      fullWidth
                      options={stickerOptions}
                      required
                      onChange={(value) => setStickerInfoGuid(value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextFieldElement
                      name='startIndex'
                      label='Upphafs reitur'
                      fullWidth
                      type='number'
                      inputProps={{
                        value: startIndex ?? undefined,
                        min: 1,
                        onChange: (event) =>
                          handleIndexChange(event.currentTarget.value),
                        disabled: startLine && startLine > 0,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextFieldElement
                      name='startLine'
                      label='Upphafs lína'
                      fullWidth
                      type='number'
                      inputProps={{
                        value: startLine ?? undefined,
                        min: 1,
                        onChange: (event) =>
                          handleLineChange(event.currentTarget.value),
                        disabled: startIndex && startIndex > 0,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextFieldElement
                      name='afrit'
                      label='Fjöldi afrita'
                      fullWidth
                      type='number'
                      inputProps={{
                        value: copies,
                        min: 1,
                        onChange: (event) =>
                          handleCopiesChange(event.currentTarget.value),
                      }}
                    />
                  </Grid>
                  <Grid container justifyContent='flex-end' py={2}>
                    <Box>
                      <Button variant='outlined' onClick={() => setToFalse()}>
                        Til Baka
                      </Button>
                      <Button
                        variant='contained'
                        type={'submit'}
                        sx={{ ml: 2 }}
                      >
                        Sækja
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </KistillFormContainer>
          </DialogContent>
        </Dialog>
      </Draggable>
    </Box>
  )
}
